// exercises.js — exercise-level behaviour on top of the per-item widgets.
//
// The exercises.lua filter wraps every heading-plus-gaps section in
// <div class="exercise" id="…"> and emits a hidden #ph-exercise-config with the
// page options. This module reads both and adds:
//
//   deferred check   no green per keystroke; a «Prüfen» button per exercise,
//                    «n von m beim ersten Versuch», a limited number of attempts,
//                    then the answer is revealed and the gap flagged for review.
//                    Items are typed gaps, bag-of-words fields and multiple choice.
//   estimate         a «Schätzung: … von N» field before the first check of any
//                    exercise with six or more items, compared afterwards
//   guess            an exercise whose heading starts with «Vermutung» (or
//                    carries {.guess}) is a generation step: one attempt, no red
//                    list, and the explanation tables after it stay locked
//                    until it has been checked — generate before you see
//   red list         first-try misses are kept in localStorage per page and
//                    listed at the end («Meine roten Formen»), copyable
//   revisit block    on a later visit the misses that are at least a day old
//                    (and, after two days, a random sample of the whole page)
//                    are asked again at the top, before anything else
//   fold             explanation tables fold away when an exercise gains focus;
//                    opening one again counts as a look-up
//   shuffle          rows with gaps are shuffled per load; rows without stay put
//   mcq              multiple choice on practice pages: options shuffled per
//                    load, checked live or with the exercise
//
// Everything is client-side and per browser. Pages without the config div —
// every handout built before this — are left exactly as they were.

import { gapAnswerAttrs, gapMatches, gapSolution } from './gap.js';
import { launchConfetti, scoreBagOfWords } from './bagofwords.js';

const DAY = 86400e3;
const REVISIT_MIN_RED = 3;   // misses needed before a revisit block appears
const REVISIT_SIZE = 10;     // items in the revisit block
const RETEST_DAYS = 2;       // age of the last visit that adds a random sample
const REVISIT_DAYS = 1;      // age of a miss before it is asked again

const L = {
    de: {
        check: 'Prüfen',
        checkAgain: 'Nochmals prüfen',
        empty: (n) => `Noch ${n} ${n === 1 ? 'Feld' : 'Felder'} leer`,
        anyway: 'trotzdem prüfen',
        firstTry: (c, n) => `${c} von ${n} beim ersten Versuch`,
        guessed: (c, n) => `${c} von ${n} richtig vermutet — jetzt die Tabelle`,
        predicted: (p) => `geschätzt ${p}`,
        predict: 'Schätzung:',
        of: 'von',
        revealed: (n) => `${n} aufgedeckt`,
        hints: (n) => `${n}× nachgeschaut`,
        again: 'Falsche Felder nochmals.',
        lastTry: 'Falsche Felder nochmals — letzter Versuch.',
        redTitle: 'Meine roten Formen',
        redIntro: 'Beim ersten Versuch falsch. Beim nächsten Besuch kommen sie zuerst dran — bis sie sitzen.',
        redEmpty: 'Noch nichts — oder alles beim ersten Versuch richtig.',
        copy: 'Kopieren',
        copied: 'Kopiert',
        clear: 'Liste löschen',
        revisitTitle: 'Wiederholung aus dem Kopf',
        revisitIntro: 'Deine roten Formen vom letzten Mal, ohne Hochscrollen. Was jetzt sitzt, verschwindet von der Liste.',
        retestIntro: 'Seit dem letzten Besuch sind mindestens zwei Tage vergangen. Zehn Formen von der ganzen Seite, gemischt — was du davon noch kannst, kannst du wirklich.',
        skip: 'Überspringen',
        colEx: 'Übung',
        colTask: 'Aufgabe',
        colAnswer: 'Antwort',
        colForm: 'Form',
        fold: 'Tabelle',
        lookup: 'Nachschauen',
        locked: 'Tabelle — erst die Vermutung prüfen',
    },
    en: {
        check: 'Check',
        checkAgain: 'Check again',
        empty: (n) => `${n} ${n === 1 ? 'field' : 'fields'} still empty`,
        anyway: 'check anyway',
        firstTry: (c, n) => `${c} of ${n} on the first try`,
        guessed: (c, n) => `${c} of ${n} guessed right — now the table`,
        predicted: (p) => `estimated ${p}`,
        predict: 'Estimate:',
        of: 'of',
        revealed: (n) => `${n} revealed`,
        hints: (n) => `looked up ${n}×`,
        again: 'Wrong fields once more.',
        lastTry: 'Wrong fields once more — last attempt.',
        redTitle: 'My misses',
        redIntro: 'Wrong on the first try. They come first on your next visit — until they stick.',
        redEmpty: 'Nothing yet — or everything right on the first try.',
        copy: 'Copy',
        copied: 'Copied',
        clear: 'Clear list',
        revisitTitle: 'Recall from memory',
        revisitIntro: 'Your misses from last time, without scrolling up. What you get now leaves the list.',
        retestIntro: 'At least two days since your last visit. Ten items from the whole page, mixed — what you still know, you really know.',
        skip: 'Skip',
        colEx: 'Exercise',
        colTask: 'Task',
        colAnswer: 'Answer',
        colForm: 'Form',
        fold: 'Table',
        lookup: 'Look up',
        locked: 'Table — check your guess first',
    },
};

function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
}

function shuffleInPlace(list) {
    for (let i = list.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
}

function shakeNode(node) {
    node.style.position = 'relative';
    node.style.left = '-4px';
    window.setTimeout(() => { node.style.left = '4px'; }, 50);
    window.setTimeout(() => { node.style.left = '0px'; }, 100);
}

function decodeB64(s) {
    try {
        return decodeURIComponent(atob((s || '').replace(/\s+/g, '')));
    } catch (_) {
        return '';
    }
}

function isGuessRoot(root) {
    return root.classList.contains('guess') || /^(vermutung|guess)/.test(root.id || '');
}

// A «Kür» exercise is optional extra work; its gaps are not drawn into the
// revisit sample, so a weak student is never re-asked what they never did.
function isOptionalRoot(root) {
    return root.classList.contains('optional') || /^(kuer|kür|optional)/.test(root.id || '');
}

// ── storage ──────────────────────────────────────────────────────────────

function storageKey() {
    const path = (window.location && window.location.pathname) || 'page';
    return `ph-ex:${path}`;
}

function loadStore() {
    try {
        const raw = window.localStorage.getItem(storageKey());
        if (raw) {
            const data = JSON.parse(raw);
            if (data && data.v === 1) return data;
        }
    } catch (_) { /* private mode, blocked storage: run without memory */ }
    return { v: 1, ex: {}, red: [], lastVisit: null };
}

function saveStore(store) {
    try {
        window.localStorage.setItem(storageKey(), JSON.stringify(store));
    } catch (_) { /* nothing to do */ }
}

// ── describing a gap for the red list ────────────────────────────────────

function cellText(cell) {
    const clone = cell.cloneNode(true);
    clone.querySelectorAll('input, textarea').forEach(n => n.replaceWith('…'));
    return clone.textContent.replace(/\s+/g, ' ').trim();
}

export function describeGap(input) {
    const td = input.closest('td, th');
    if (td) {
        const tr = td.parentElement;
        const cells = Array.from(tr.children);
        const idx = cells.indexOf(td);
        const table = input.closest('table');
        const parts = [];
        cells.slice(0, idx).forEach((c) => {
            if (!c.querySelector('input, textarea')) {
                const t = cellText(c);
                if (t) parts.push(t);
            }
        });
        const headRow = table && table.querySelector('thead tr');
        const th = headRow && headRow.children[idx];
        const col = th ? cellText(th) : '';
        if (col) parts.push(col);
        const own = cellText(td);
        if (own && own !== '…') parts.push(own);
        const text = parts.join(' · ');
        if (text) return text.slice(0, 90);
    }
    const host = input.closest('p, li, dd, td, div');
    if (host) {
        const clone = host.cloneNode(true);
        clone.querySelectorAll('input, textarea').forEach((n) => {
            n.replaceWith(n === input || n.isEqualNode(input) ? '____' : '…');
        });
        const text = clone.textContent.replace(/\s+/g, ' ').trim();
        if (text) return text.slice(0, 120);
    }
    return '';
}

// ── items: one interface over gaps, bag-of-words fields and MCQs ─────────

function gapItem(input) {
    return {
        kind: 'gap',
        node: input,
        done: false,
        isEmpty: () => !input.value.trim(),
        isOk: () => gapMatches(input, input.value),
        focus: () => input.focus(),
        mark: (ok) => {
            input.classList.remove('gap-input-filled', ok ? 'gap-input-incorrect' : 'gap-input-correct');
            input.classList.add(ok ? 'gap-input-correct' : 'gap-input-incorrect');
            if (ok) input.disabled = true; else shakeNode(input);
        },
        reveal: () => {
            input.value = gapSolution(input);
            input.classList.remove('gap-input-incorrect', 'gap-input-filled');
            input.classList.add('gap-input-revealed');
            input.disabled = true;
        },
    };
}

function bowItem(textarea) {
    // The progress bar is a sibling that bagofwords.js adds later; CSS hides it
    // while the field is deferred and unchecked (see .ph-ex-checked).
    const show = (score) => {
        textarea.classList.add('ph-ex-checked');
        const status = textarea.nextElementSibling;
        if (!status || !status.classList.contains('bag-success')) return;
        const bar = status.querySelector('.bag-progress-bar');
        const text = status.querySelector('.bag-progress-text');
        if (bar) bar.style.width = `${score.percentage}%`;
        if (text) text.textContent = score.exactMatch ? score.solution : `${score.percentage}%`;
    };
    return {
        kind: 'bow',
        node: textarea,
        done: false,
        isEmpty: () => !textarea.value.trim(),
        isOk: () => scoreBagOfWords(textarea).exactMatch,
        focus: () => textarea.focus(),
        mark: (ok) => {
            const score = scoreBagOfWords(textarea);
            show(score);
            textarea.classList.remove('gap-input-filled', ok ? 'gap-input-incorrect' : 'gap-input-correct');
            textarea.classList.add(ok ? 'gap-input-correct' : 'gap-input-incorrect');
            if (ok) { textarea.disabled = true; textarea.dataset.completed = 'true'; } else shakeNode(textarea);
        },
        reveal: () => {
            const score = scoreBagOfWords(textarea);
            textarea.value = score.solution;
            show({ exactMatch: true, percentage: 100, solution: score.solution });
            textarea.classList.remove('gap-input-incorrect', 'gap-input-filled');
            textarea.classList.add('gap-input-revealed');
            textarea.disabled = true;
        },
    };
}

function mcqItem(fieldset) {
    const radios = () => Array.from(fieldset.querySelectorAll('input[type=radio]'));
    const chosen = () => radios().find(r => r.checked);
    const solution = decodeB64(fieldset.dataset.solution);
    return {
        kind: 'mcq',
        node: fieldset,
        done: false,
        isEmpty: () => !chosen(),
        isOk: () => { const c = chosen(); return !!c && c.value === solution; },
        focus: () => { const r = radios()[0]; if (r) r.focus(); },
        mark: (ok) => {
            fieldset.classList.remove(ok ? 'ph-mcq-wrong' : 'ph-mcq-right');
            fieldset.classList.add(ok ? 'ph-mcq-right' : 'ph-mcq-wrong');
            if (ok) radios().forEach(r => { r.disabled = true; }); else shakeNode(fieldset);
        },
        reveal: () => {
            radios().forEach((r) => {
                r.disabled = true;
                if (r.value === solution) {
                    r.checked = true;
                    r.closest('label').classList.add('ph-mcq-correct');
                }
            });
            fieldset.classList.remove('ph-mcq-wrong');
            fieldset.classList.add('ph-mcq-revealed');
        },
    };
}

function shuffleMcqOptions(fieldset) {
    const labels = Array.from(fieldset.querySelectorAll(':scope > label.ph-mcq-opt'));
    if (labels.length < 2) return;
    shuffleInPlace(labels).forEach(l => fieldset.appendChild(l));
}

// Live check for an MCQ outside any deferred exercise: like a live gap.
function bindLiveMcq(fieldset) {
    const solution = decodeB64(fieldset.dataset.solution);
    fieldset.addEventListener('change', (event) => {
        const r = event.target;
        if (!r || r.type !== 'radio') return;
        const ok = r.value === solution;
        fieldset.classList.toggle('ph-mcq-right', ok);
        fieldset.classList.toggle('ph-mcq-wrong', !ok);
        if (ok) {
            fieldset.querySelectorAll('input[type=radio]').forEach(x => { x.disabled = true; });
            launchConfetti();
        } else {
            shakeNode(fieldset);
        }
    });
}

// ── exercise ─────────────────────────────────────────────────────────────

class Exercise {
    constructor(root, ctx) {
        this.root = root;
        this.ctx = ctx;
        this.id = root.id;
        this.title = root.dataset.title || root.id;
        this.guess = isGuessRoot(root);
        this.maxAttempts = this.guess ? 1 : (parseInt(root.dataset.attempts || '', 10) || ctx.attempts);
        this.generated = root.classList.contains('ph-ex-generated');
        this.attempts = 0;
        this.hints = 0;

        const own = (node) => node.closest('.exercise') === root;
        this.inputs = Array.from(root.querySelectorAll('input.gap-input[data-solution]')).filter(own);
        const bows = Array.from(root.querySelectorAll('textarea.bagofwords[data-solution]')).filter(own);
        const mcqs = Array.from(root.querySelectorAll('.ph-mcq[data-solution]')).filter(own);

        this.inputs.forEach((input, idx) => {
            input.dataset.phDeferred = '1';
            if (!input.dataset.phGap) input.dataset.phGap = String(idx);
            input.addEventListener('input', () => input.classList.remove('gap-input-incorrect'));
        });
        bows.forEach((t) => { t.dataset.phDeferred = '1'; });
        mcqs.forEach((f) => { f.dataset.phDeferred = '1'; f.addEventListener('change', () => f.classList.remove('ph-mcq-wrong')); });

        this.items = [
            ...this.inputs.map(gapItem),
            ...bows.map(bowItem),
            ...mcqs.map(mcqItem),
        ];

        const store = ctx.store;
        this.state = store.ex[this.id] || (store.ex[this.id] = {
            first: null, predicted: null, hints: 0, done: false, ts: null,
        });

        this.buildBar();
    }

    get total() {
        return this.items.length;
    }

    get checked() {
        return this.attempts > 0 || this.state.first != null;
    }

    buildBar() {
        const t = this.ctx.t;
        const bar = el('div', 'ph-ex-bar');
        bar.setAttribute('role', 'group');

        if (this.ctx.predict && !this.guess && this.total >= 6 && this.state.first == null) {
            const label = el('label', 'ph-ex-predict');
            label.append(`${t.predict} `);
            const field = el('input');
            field.type = 'number';
            field.min = '0';
            field.max = String(this.total);
            field.inputMode = 'numeric';
            field.setAttribute('aria-label', t.predict);
            label.append(field, ` ${t.of} ${this.total}`);
            bar.append(label);
            this.predictField = field;
        }

        const btn = el('button', 'ph-ex-check', t.check);
        btn.type = 'button';
        btn.addEventListener('click', () => this.check(false));
        this.button = btn;

        const result = el('span', 'ph-ex-result');
        result.setAttribute('role', 'status');
        result.setAttribute('aria-live', 'polite');
        this.result = result;

        bar.append(btn, result);
        this.root.append(bar);
        this.bar = bar;
    }

    check(force) {
        const t = this.ctx.t;
        const open = this.items.filter(i => !i.done);
        if (!open.length) return;

        const empties = open.filter(i => i.isEmpty());
        if (empties.length && !force) {
            this.result.textContent = `${t.empty(empties.length)} · `;
            const anyway = el('button', 'ph-ex-link', t.anyway);
            anyway.type = 'button';
            anyway.addEventListener('click', () => this.check(true));
            this.result.append(anyway);
            empties[0].focus();
            return;
        }

        this.attempts += 1;
        const wrong = [];
        open.forEach((item) => {
            const ok = item.isOk();
            item.mark(ok);
            if (ok) item.done = true; else wrong.push(item);
        });

        // The first check of a session is the honest measure; the red list
        // learns from every session, so a page revisited later counts anew.
        if (this.attempts === 1) {
            const predicted = this.predictField ? parseInt(this.predictField.value, 10) : NaN;
            this.state.first = { wrong: wrong.map(i => this.items.indexOf(i)), total: this.total };
            this.state.predicted = Number.isFinite(predicted) ? predicted : null;
            this.state.ts = Date.now();
            if (this.predictField) this.predictField.disabled = true;
            if (!this.guess) this.ctx.onFirstTry(this, wrong);
            this.ctx.onChecked(this);
        }

        let revealed = 0;
        if (wrong.length && this.attempts >= this.maxAttempts) {
            wrong.forEach((item) => { item.reveal(); item.done = true; revealed += 1; });
        }

        const done = wrong.length - revealed === 0;
        if (done) {
            this.state.done = true;
            this.button.disabled = true;
            if (!this.guess && this.state.first && this.state.first.wrong.length === 0) launchConfetti();
        } else {
            this.button.textContent = t.checkAgain;
            wrong[0].focus();
        }

        this.state.hints = this.hints;
        this.renderResult(done ? revealed : 0, done ? '' : (this.attempts >= this.maxAttempts - 1 ? t.lastTry : t.again));
        this.ctx.save();
    }

    renderResult(revealed, pendingText) {
        const t = this.ctx.t;
        const first = this.state.first;
        const parts = [];
        if (first) {
            const right = first.total - first.wrong.length;
            parts.push(this.guess ? t.guessed(right, first.total) : t.firstTry(right, first.total));
            if (this.state.predicted != null) parts.push(t.predicted(this.state.predicted));
        }
        if (this.hints) parts.push(t.hints(this.hints));
        if (revealed && !this.guess) parts.push(t.revealed(revealed));
        this.result.textContent = parts.join(' · ');
        if (pendingText) this.result.append(` — ${pendingText}`);
    }

    noteHint() {
        this.hints += 1;
    }
}

// ── page-level pieces ────────────────────────────────────────────────────

function precedingExercise(node, roots) {
    let found = null;
    roots.forEach((root) => {
        if (root.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING) found = root;
    });
    return found;
}

function setupFold(ctx, roots) {
    const skip = (table) => table.closest('.exercise, .ph-ex-bar, .ph-ex-redlist, details.ph-fold');
    const targets = Array.from(document.querySelectorAll('table')).filter(t => !skip(t));
    document.querySelectorAll('div.fold').forEach((div) => {
        if (!div.closest('.exercise')) targets.push(div);
    });
    if (!targets.length) return;

    const folds = targets.map((node) => {
        const details = el('details', 'ph-fold');
        details.open = true;
        const summary = el('summary', 'ph-fold-summary', ctx.t.fold);
        node.parentNode.insertBefore(details, node);
        details.append(summary, node);

        // A table right after a «Vermutung» exercise stays shut until that
        // guess has been checked: generate first, then look.
        const owner = precedingExercise(details, roots);
        const guess = owner && ctx.byRoot.get(owner);
        if (guess && guess.guess && !guess.checked) {
            details.open = false;
            details.dataset.lockedBy = owner.id;
            details.classList.add('ph-fold-locked');
            summary.textContent = ctx.t.locked;
        }

        details.addEventListener('toggle', () => {
            if (!details.open) return;
            if (details.dataset.lockedBy) {
                details.open = false;
                return;
            }
            if (ctx.focused) {
                ctx.focused.noteHint();
                summary.textContent = `${ctx.t.fold} · ${ctx.t.lookup} ✓`;
            }
        });
        return details;
    });

    ctx.unlock = (ex) => {
        folds.forEach((d) => {
            if (d.dataset.lockedBy === ex.id) {
                delete d.dataset.lockedBy;
                d.classList.remove('ph-fold-locked');
                d.querySelector('summary').textContent = ctx.t.fold;
                d.open = true;
            }
        });
    };

    document.addEventListener('focusin', (event) => {
        const root = event.target.closest && event.target.closest('.exercise');
        if (!root) return;
        const ex = ctx.byRoot.get(root);
        if (ex) ctx.focused = ex;
        folds.forEach((d) => { if (d.open) d.open = false; });
    });
}

function setupShuffle() {
    document.querySelectorAll('.exercise table tbody').forEach((tbody) => {
        const rows = Array.from(tbody.querySelectorAll(':scope > tr'));
        const movable = rows.filter(r => r.querySelector('input, textarea'));
        if (movable.length < 2) return;
        const order = shuffleInPlace(movable.slice());
        const slots = rows.map(r => (movable.includes(r) ? null : r));
        let k = 0;
        const result = slots.map(r => r || order[k++]);
        result.forEach(r => tbody.appendChild(r));
    });
}

function findSourceInput(ref) {
    const root = document.getElementById(ref.ex);
    if (!root) return null;
    return root.querySelector(`input.gap-input[data-ph-gap="${ref.gap}"]`);
}

function buildRevisit(ctx, firstRoot) {
    const t = ctx.t;
    const store = ctx.store;
    const now = Date.now();
    const oldRed = store.red.filter(r => now - r.ts >= REVISIT_DAYS * DAY && findSourceInput(r));
    const stale = store.lastVisit != null && now - store.lastVisit >= RETEST_DAYS * DAY;
    if (oldRed.length < REVISIT_MIN_RED && !stale) return null;

    const items = oldRed.slice(0, REVISIT_SIZE).map(r => ({ ref: r, input: findSourceInput(r), red: true }));
    if (stale && items.length < REVISIT_SIZE) {
        const taken = new Set(items.map(i => i.input));
        const pool = Array.from(document.querySelectorAll('.exercise:not(.ph-ex-generated) input.gap-input[data-solution]'))
            .filter(i => !taken.has(i) && !isGuessRoot(i.closest('.exercise')) && !isOptionalRoot(i.closest('.exercise')));
        shuffleInPlace(pool).slice(0, REVISIT_SIZE - items.length).forEach((input) => {
            const root = input.closest('.exercise');
            items.push({ ref: { ex: root.id, gap: input.dataset.phGap, title: root.dataset.title || root.id }, input, red: false });
        });
    }
    if (!items.length) return null;
    shuffleInPlace(items);

    const block = el('div', 'exercise ph-ex-generated ph-ex-revisit');
    block.id = 'revisit';
    block.dataset.title = t.revisitTitle;
    block.append(el('h2', null, t.revisitTitle));
    block.append(el('p', null, stale && oldRed.length < REVISIT_MIN_RED ? t.retestIntro : t.revisitIntro));

    const table = el('table', 'ph-ex-revisit-table');
    const thead = el('thead');
    const hr = el('tr');
    hr.append(el('th', null, t.colTask), el('th', null, t.colForm));
    thead.append(hr);
    const tbody = el('tbody');
    items.forEach((item) => {
        const tr = el('tr');
        const prompt = item.ref.prompt || describeGap(item.input);
        const title = item.ref.title || '';
        const task = el('td');
        task.append(prompt);
        if (title) {
            task.append(' ');
            task.append(el('small', 'ph-ex-origin', `(${title})`));
        }
        const input = document.createElement('input');
        input.className = 'gap-input';
        const answers = gapAnswerAttrs(item.input);
        input.setAttribute('data-solution', answers.solution);
        input.setAttribute('data-solution-base64', answers.base64);
        input.setAttribute('placeholder', item.input.getAttribute('placeholder') || '');
        input.setAttribute('autocorrect', 'off');
        input.setAttribute('autocapitalize', 'none');
        input.setAttribute('spellcheck', 'false');
        input.size = item.input.size || 8;
        input.dataset.phSrcEx = item.ref.ex;
        input.dataset.phSrcGap = String(item.ref.gap);
        input.dataset.phSrcRed = item.red ? '1' : '0';
        const form = el('td');
        form.append(input);
        tr.append(task, form);
        tbody.append(tr);
    });
    table.append(thead, tbody);
    block.append(table);

    const skip = el('button', 'ph-ex-link ph-ex-skip', t.skip);
    skip.type = 'button';
    skip.addEventListener('click', () => block.remove());
    block.append(skip);

    firstRoot.parentNode.insertBefore(block, firstRoot);
    return block;
}

function buildRedList(ctx, afterNode) {
    const t = ctx.t;
    const box = el('section', 'ph-ex-redlist');
    box.append(el('h2', null, t.redTitle));
    box.append(el('p', null, t.redIntro));
    const table = el('table');
    const thead = el('thead');
    const hr = el('tr');
    hr.append(el('th', null, t.colEx), el('th', null, t.colTask), el('th', null, t.colAnswer));
    thead.append(hr);
    const tbody = el('tbody');
    table.append(thead, tbody);
    const empty = el('p', 'ph-ex-redlist-empty', t.redEmpty);
    box.append(table, empty);

    const actions = el('div', 'ph-ex-redlist-actions');
    const copy = el('button', 'ph-ex-link', t.copy);
    copy.type = 'button';
    copy.addEventListener('click', () => {
        const text = ctx.store.red.map(r => `${r.title}\t${r.prompt}\t${r.answer}`).join('\n');
        const done = () => { copy.textContent = t.copied; window.setTimeout(() => { copy.textContent = t.copy; }, 1500); };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(done, () => {});
        }
    });
    const clear = el('button', 'ph-ex-link', t.clear);
    clear.type = 'button';
    clear.addEventListener('click', () => {
        ctx.store.red = [];
        ctx.save();
        ctx.renderRed();
    });
    actions.append(copy, clear);
    box.append(actions);

    afterNode.parentNode.insertBefore(box, afterNode.nextSibling);

    ctx.renderRed = () => {
        tbody.textContent = '';
        ctx.store.red.forEach((r) => {
            const tr = el('tr');
            tr.append(el('td', null, r.title), el('td', null, r.prompt), el('td', 'ph-ex-answer', r.answer));
            tbody.append(tr);
        });
        const has = ctx.store.red.length > 0;
        table.hidden = !has;
        empty.hidden = has;
        actions.hidden = !has;
    };
    ctx.renderRed();
    return box;
}

export function initExercises() {
    const config = document.getElementById('ph-exercise-config');
    if (!config) return;

    const lang = (config.dataset.lang || 'de').slice(0, 2);
    const ctx = {
        t: L[lang] || L.de,
        deferred: config.dataset.check === 'deferred',
        attempts: parseInt(config.dataset.attempts || '2', 10) || 2,
        fold: config.dataset.fold === '1',
        shuffle: config.dataset.shuffle === '1',
        predict: true,
        store: loadStore(),
        byRoot: new Map(),
        focused: null,
        renderRed: () => {},
        unlock: () => {},
        save: () => saveStore(ctx.store),
        onFirstTry: () => {},
        onChecked: (ex) => ctx.unlock(ex),
    };

    const roots = Array.from(document.querySelectorAll('.exercise'));
    console.log('[exercises] init', { count: roots.length, deferred: ctx.deferred });

    // Stable gap numbers in document order, before anything is moved.
    roots.forEach((root) => {
        Array.from(root.querySelectorAll('input.gap-input[data-solution]'))
            .filter(i => i.closest('.exercise') === root)
            .forEach((input, idx) => { input.dataset.phGap = String(idx); });
    });

    // Multiple choice on a practice page: options shuffled per load.
    const mcqs = Array.from(document.querySelectorAll('.ph-mcq[data-solution]'));
    mcqs.forEach(shuffleMcqOptions);

    if (ctx.shuffle) setupShuffle();

    if (ctx.deferred && roots.length) {
        ctx.onFirstTry = (ex, wrongItems) => {
            const now = Date.now();
            wrongItems.forEach((item) => {
                if (item.kind !== 'gap') return; // sentences and choices are not list items
                const input = item.node;
                const ref = ex.generated
                    ? { ex: input.dataset.phSrcEx, gap: input.dataset.phSrcGap }
                    : { ex: ex.id, gap: input.dataset.phGap };
                const source = ex.generated ? findSourceInput(ref) : input;
                const existing = ctx.store.red.find(r => r.ex === ref.ex && String(r.gap) === String(ref.gap));
                if (existing) {
                    existing.ts = now;
                } else if (source) {
                    const srcRoot = source.closest('.exercise');
                    ctx.store.red.push({
                        ex: ref.ex,
                        gap: String(ref.gap),
                        title: (srcRoot && srcRoot.dataset.title) || ref.ex,
                        prompt: describeGap(source),
                        answer: gapSolution(source),
                        ts: now,
                    });
                }
            });
            if (ex.generated) {
                // a miss answered right on the first try in the revisit block has been learnt
                const wrongNodes = new Set(wrongItems.map(i => i.node));
                ex.inputs.forEach((input) => {
                    if (wrongNodes.has(input) || input.dataset.phSrcRed !== '1') return;
                    ctx.store.red = ctx.store.red.filter(r => !(r.ex === input.dataset.phSrcEx && String(r.gap) === input.dataset.phSrcGap));
                });
            }
            ctx.save();
            ctx.renderRed();
        };

        const revisit = buildRevisit(ctx, roots[0]);
        const all = revisit ? [revisit, ...roots] : roots;
        all.forEach((root) => {
            const ex = new Exercise(root, ctx);
            if (ex.total === 0) {
                ex.bar.remove();
                return;
            }
            ctx.byRoot.set(root, ex);
        });
        buildRedList(ctx, roots[roots.length - 1]);
    } else {
        mcqs.forEach(bindLiveMcq);
    }

    if (ctx.fold) setupFold(ctx, roots);

    ctx.store.lastVisit = Date.now();
    ctx.save();
}
